/**
 * SearchSuggestionsDropdown.jsx - Real-time search suggestions dropdown
 * 
 * Reusable component that shows autocomplete suggestions as the user types.
 * Integrates with the /suggest Cloud Function endpoint for fast (<100ms) lookups.
 * 
 * Features:
 * - Debounced input (300ms) to avoid excessive network calls
 * - Keyboard navigation (Arrow Up/Down, Enter, Escape)
 * - Touch/click selection
 * - Animated entrance/exit
 * - Glass-morphism styling matching app theme
 * - AbortController for cancelling stale requests
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform, Animated } from 'react-native';
import { fetchSuggestions } from '../services/cacheService';
import { isMobile } from '../utils/responsive';

/**
 * Suggestion type icons/badges
 */
const SUGGESTION_ICONS = {
  player: '🏅',
  player_market: '📊',
  team: '🏟️',
  market: '📈',
  action: '⚡',
  sportsbook: '🎰',
};

/**
 * SearchSuggestionsDropdown
 * 
 * @param {Object} props
 * @param {string} props.query - Current search input value
 * @param {Function} props.onSelect - Called when user selects a suggestion: (suggestionText: string) => void
 * @param {Function} props.onDismiss - Called when dropdown should close (e.g., Escape key)
 * @param {boolean} props.visible - Whether dropdown should be visible (controlled externally)
 * @param {string} props.sport - Optional sport filter for suggestions
 * @param {Object} props.style - Optional container style overrides
 */
export default function SearchSuggestionsDropdown({ query, onSelect, onDismiss, visible = true, sport = null, style = {} }) {
  const [suggestions, setSuggestions] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [loading, setLoading] = useState(false);
  const abortRef = useRef(null);
  const debounceRef = useRef(null);
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const lastQueryRef = useRef('');

  // ═══════════════════════════════════════════════════════════
  // DEBOUNCED FETCH - 300ms delay, cancels stale requests
  // ═══════════════════════════════════════════════════════════
  const debouncedFetch = useCallback((partial) => {
    // Clear previous debounce
    if (debounceRef.current) clearTimeout(debounceRef.current);

    // Abort previous in-flight request
    if (abortRef.current) abortRef.current.abort();

    if (!partial || partial.trim().length < 2) {
      setSuggestions([]);
      setSelectedIndex(-1);
      return;
    }

    // Don't refetch if same query
    if (partial.trim() === lastQueryRef.current) return;

    debounceRef.current = setTimeout(async () => {
      const controller = new AbortController();
      abortRef.current = controller;

      setLoading(true);
      try {
        const results = await fetchSuggestions(partial, {
          sport,
          limit: 8,
          signal: controller.signal,
        });
        lastQueryRef.current = partial.trim();
        setSuggestions(results);
        setSelectedIndex(-1);
      } catch (err) {
        // AbortError is expected, ignore
      } finally {
        setLoading(false);
      }
    }, 300);
  }, [sport]);

  // Trigger fetch when query changes
  useEffect(() => {
    debouncedFetch(query);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, [query, debouncedFetch]);

  // ═══════════════════════════════════════════════════════════
  // ANIMATE IN/OUT
  // ═══════════════════════════════════════════════════════════
  const showDropdown = visible && suggestions.length > 0;

  useEffect(() => {
    Animated.timing(fadeAnim, {
      toValue: showDropdown ? 1 : 0,
      duration: 150,
      useNativeDriver: Platform.OS !== 'web',
    }).start();
  }, [showDropdown, fadeAnim]);

  // ═══════════════════════════════════════════════════════════
  // KEYBOARD NAVIGATION (web only)
  // ═══════════════════════════════════════════════════════════
  useEffect(() => {
    if (Platform.OS !== 'web' || !showDropdown) return;

    const handleKeyDown = (e) => {
      if (!showDropdown) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, suggestions.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, -1));
      } else if (e.key === 'Enter' && selectedIndex >= 0) {
        e.preventDefault();
        e.stopPropagation();
        handleSelect(suggestions[selectedIndex]);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onDismiss?.();
        setSuggestions([]);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showDropdown, suggestions, selectedIndex]);

  // ═══════════════════════════════════════════════════════════
  // SELECTION HANDLER
  // ═══════════════════════════════════════════════════════════
  const handleSelect = (suggestion) => {
    onSelect?.(suggestion.text);
    setSuggestions([]);
    setSelectedIndex(-1);
    lastQueryRef.current = suggestion.text; // Prevent re-fetch of selected text
  };

  if (!showDropdown) return null;

  return (
    <Animated.View style={[
      styles.container,
      style,
      { opacity: fadeAnim, transform: [{ translateY: fadeAnim.interpolate({ inputRange: [0, 1], outputRange: [-4, 0] }) }] },
    ]}>
      {suggestions.filter(s => s && s.text).map((suggestion, index) => (
        <TouchableOpacity
          key={`${suggestion.text}-${index}`}
          style={[
            styles.suggestionRow,
            index === selectedIndex && styles.suggestionRowSelected,
            index === suggestions.length - 1 && styles.suggestionRowLast,
          ]}
          onPress={() => handleSelect(suggestion)}
          activeOpacity={0.7}
        >
          <Text style={styles.suggestionIcon}>
            {SUGGESTION_ICONS[suggestion.type] || '🔍'}
          </Text>
          <View style={styles.suggestionTextContainer}>
            <Text style={styles.suggestionText} numberOfLines={1}>
              {highlightMatch(suggestion.text, query)}
            </Text>
            {suggestion.team && (
              <Text style={styles.suggestionMeta} numberOfLines={1}>
                {suggestion.team}{suggestion.league ? ` · ${suggestion.league.toUpperCase()}` : ''}
              </Text>
            )}
          </View>
          <Text style={styles.suggestionType}>
            {formatType(suggestion.type)}
          </Text>
        </TouchableOpacity>
      ))}
      {loading && suggestions.length > 0 && (
        <View style={styles.loadingIndicator}>
          <Text style={styles.loadingText}>Updating...</Text>
        </View>
      )}
    </Animated.View>
  );
}

/**
 * Highlight the matching portion of text
 */
function highlightMatch(text, query) {
  if (!text) return text;
  if (!query || query.length < 2) return text;

  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase().trim();
  const idx = lowerText.indexOf(lowerQuery);

  if (idx === -1) return text;

  return (
    <Text>
      <Text>{text.substring(0, idx)}</Text>
      <Text style={{ color: '#7DF9FF', fontWeight: '700' }}>{text.substring(idx, idx + lowerQuery.length)}</Text>
      <Text>{text.substring(idx + lowerQuery.length)}</Text>
    </Text>
  );
}

/**
 * Format suggestion type for display
 */
function formatType(type) {
  const labels = {
    player: 'Player',
    player_market: 'Prop',
    team: 'Team',
    market: 'Market',
    action: 'Quick',
    sportsbook: 'Book',
  };
  return labels[type] || '';
}

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    zIndex: 9999,
    backgroundColor: 'rgba(10, 10, 20, 0.95)',
    borderRadius: isMobile ? 10 : 12,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.12)',
    marginTop: 4,
    overflow: 'hidden',
    ...(Platform.OS === 'web' ? {
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
    } : {
      elevation: 10,
    }),
  },
  suggestionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: isMobile ? 10 : 11,
    paddingHorizontal: isMobile ? 12 : 16,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.06)',
  },
  suggestionRowSelected: {
    backgroundColor: 'rgba(125, 249, 255, 0.08)',
  },
  suggestionRowLast: {
    borderBottomWidth: 0,
  },
  suggestionIcon: {
    fontSize: isMobile ? 14 : 16,
    marginRight: isMobile ? 8 : 10,
    width: 22,
    textAlign: 'center',
  },
  suggestionTextContainer: {
    flex: 1,
    marginRight: 8,
  },
  suggestionText: {
    color: 'rgba(255, 255, 255, 0.92)',
    fontSize: isMobile ? 13 : 14,
    fontWeight: '500',
  },
  suggestionMeta: {
    color: 'rgba(255, 255, 255, 0.4)',
    fontSize: isMobile ? 11 : 12,
    marginTop: 2,
  },
  suggestionType: {
    color: 'rgba(125, 249, 255, 0.6)',
    fontSize: isMobile ? 10 : 11,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  loadingIndicator: {
    paddingVertical: 6,
    alignItems: 'center',
  },
  loadingText: {
    color: 'rgba(255, 255, 255, 0.3)',
    fontSize: 11,
  },
});
